import React from 'react';
import { HealthArticle, HealthCheckDefinition, HealthReport } from '../../../../services/performanceMetricsService';
import { ArticleCard } from './ArticleCard';
import { fmtDate } from './healthCheckHelpers';

export const ArticlesTab: React.FC<{
  report: HealthReport | null;
  checks: HealthCheckDefinition[];
  onStartRefresh: (article: HealthArticle) => void;
  onViewDetails: (url: string) => void;
  startingRefreshUrl: string | null;
}> = ({ report, checks, onStartRefresh, onViewDetails, startingRefreshUrl }) => {
  if (!report || report.status === 'never_run') {
    return (
      <div className="text-center py-12 text-gray-400">
        <p className="text-lg mb-2">No health report yet</p>
        <p className="text-sm">Click "Run Health Checks" to generate the first report.</p>
      </div>
    );
  }

  const notIndexed = report.not_indexed || [];
  const articles = report.articles || [];
  const checkLookup = Object.fromEntries(checks.map((c) => [c.id, c]));

  return (
    <div>
      {/* Tier summary */}
      <div className="mb-4">
        <p className="text-sm text-gray-500 mb-2">{report.total_articles_checked} articles checked.</p>
        {report.tiers && (
          <div className="flex items-center gap-3 text-sm">
            <span className="px-2.5 py-1 rounded bg-red-50 text-red-700 font-medium">
              {report.tiers.urgent} urgent
            </span>
            <span className="px-2.5 py-1 rounded bg-amber-50 text-amber-700 font-medium">
              {report.tiers.needs_attention} needs attention
            </span>
            <span className="px-2.5 py-1 rounded bg-yellow-50 text-yellow-700 font-medium">
              {report.tiers.minor} minor
            </span>
            <span className="px-2.5 py-1 rounded bg-green-50 text-green-700 font-medium">
              {report.tiers.healthy} healthy
            </span>
            {notIndexed.length > 0 && (
              <span className="px-2.5 py-1 rounded bg-red-100 text-red-800 font-medium">
                {notIndexed.length} not indexed
              </span>
            )}
          </div>
        )}
      </div>

      {/* Critical tier: not indexed */}
      {notIndexed.length > 0 && (
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-red-600 text-lg font-bold">!</span>
            <h3 className="text-sm font-semibold text-red-800">
              Not Indexed ({notIndexed.length} article{notIndexed.length !== 1 ? 's' : ''})
            </h3>
            <span className="text-xs text-red-600">These articles are invisible in Google Search</span>
          </div>
          <div className="space-y-3">
            {notIndexed.map((article) => (
              <ArticleCard key={article.url} article={article} checkLookup={checkLookup} onStartRefresh={onStartRefresh} onViewDetails={() => onViewDetails(article.url)} isStartingRefresh={startingRefreshUrl === article.url} />
            ))}
          </div>
        </div>
      )}

      {/* Main tier: scored articles */}
      {articles.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Needs Attention — Top {articles.length} by severity
          </h3>
          <div className="space-y-4">
            {articles.map((article, idx) => (
              <ArticleCard key={article.url} article={article} rank={idx + 1} checkLookup={checkLookup} onStartRefresh={onStartRefresh} onViewDetails={() => onViewDetails(article.url)} isStartingRefresh={startingRefreshUrl === article.url} />
            ))}
          </div>
        </>
      )}

      {articles.length === 0 && notIndexed.length === 0 && (
        <div className="text-center py-12 text-gray-400">No articles with issues found.</div>
      )}
    </div>
  );
};
